import { useState } from "react";
import { Link } from "react-router-dom";
import HeaderBar from "./components/headerBar";
import NavTabs from "./components/navTabs";
import {
  useCoursesQuery,
  useCourseTracksQuery,
} from "../../hooks/queries/useCourseQueries";
import {
  Layers,
  Book,
  Search,
  Map as MapIcon,
  Trophy,
  Users,
  Star,
  Clock,
  ChevronRight,
  Sparkles
} from "lucide-react";
import Button from "../../components/common/Button";

const stripHtml = (html?: string) => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").replace(/&nbsp;/g, " ").trim();
};

const TrackWideCard = ({
  title,
  level,
  duration,
  image,
  slug,
  description,
  outline_snippet,
  rating,
  review_count,
  enrolled_count,
  lessons_count,
  course_in_track,
  payment_type,
  online_price,
  has_certificate = true,
}: any) => {
  const coursesCount = Array.isArray(course_in_track) ? course_in_track.length : 0;
  const totalLessons =
    (Array.isArray(course_in_track)
      ? course_in_track.reduce(
          (sum: number, c: any) =>
            sum +
            (c.modules?.reduce(
              (mSum: number, m: any) =>
                mSum +
                (m.lessons?.length || 0) +
                (m.exercises?.length || m.exercise_module?.length || 0),
              0
            ) || Number(c.lessons_count) || 0),
          0
        )
      : 0) || Number(lessons_count) || 0;
  const cleanSummary =
    stripHtml(outline_snippet) ||
    stripHtml(description) ||
    `Master the tools and techniques required to excel in ${title} with our structured sequence of courses.`;
  const displayRating = Number(rating) > 0 ? Number(rating).toFixed(1) : "0.0";
  const displayReviews = review_count ?? 0;
  const displayEnrolled = enrolled_count ?? 0;
  const displayPrice =
    payment_type === "Free"
      ? "Free"
      : online_price != null
      ? `₦${Number(online_price).toLocaleString()}`
      : payment_type || "Paid";

  return (
    <div className="cht-group cht-relative cht-bg-white cht-rounded-[28px] cht-overflow-hidden cht-shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:cht-shadow-2xl hover:cht-scale-[1.01] cht-transition-all cht-duration-500 cht-border cht-border-gray-100/50">
      <div className="cht-flex cht-flex-col xl:cht-flex-row">
        {/* Visual Side */}
        <div className="xl:cht-w-1/3 cht-relative cht-h-48 xl:cht-h-auto cht-overflow-hidden">
          <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-br cht-from-[#3e57da] cht-to-[#6a82fb] cht-opacity-90 group-hover:cht-scale-110 cht-transition-transform cht-duration-700"></div>
          {image && (
            <img
              src={image}
              alt={title}
              className="cht-w-full cht-h-full cht-object-cover cht-mix-blend-overlay cht-opacity-50"
            />
          )}
          <div className="cht-absolute cht-inset-0 cht-flex cht-flex-col cht-items-center cht-justify-center cht-text-white cht-p-6 cht-text-center">
            <MapIcon size={40} className="cht-mb-4 cht-opacity-80" />
            <span className="cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-[0.2em] cht-bg-white/20 cht-px-3 cht-py-1 cht-rounded-full cht-backdrop-blur-md cht-border cht-border-white/20">
              Learning Path
            </span>
          </div>
        </div>

        {/* Content Side */}
        <div className="xl:cht-w-2/3 cht-p-8 cht-flex cht-flex-col cht-justify-between cht-space-y-6">
          <div className="cht-flex cht-flex-col md:cht-flex-row cht-justify-between cht-items-start cht-gap-4">
            <div className="cht-space-y-2">
              <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
                <span className="cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-text-[10px] cht-font-bold cht-px-2.5 cht-py-1 cht-rounded-full cht-uppercase cht-tracking-wider cht-flex cht-items-center cht-gap-1">
                  <Sparkles size={10} /> Career Ready
                </span>
                {level && (
                  <span className="cht-bg-orange-500/10 cht-text-orange-500 cht-text-[10px] cht-font-bold cht-px-2.5 cht-py-1 cht-rounded-full cht-uppercase cht-tracking-wider">
                    {level}
                  </span>
                )}
                <span className="cht-bg-slate-100 cht-text-slate-700 cht-text-[10px] cht-font-bold cht-px-2.5 cht-py-1 cht-rounded-full cht-uppercase cht-tracking-wider">
                  {displayPrice}
                </span>
              </div>
              <h3 className="cht-text-2xl cht-font-bold cht-text-[#1e293b] cht-tracking-tight group-hover:cht-text-[#3e57da] cht-transition-colors">
                {title}
              </h3>
              <p className="cht-text-gray-500 cht-text-sm cht-font-medium cht-line-clamp-2">
                {cleanSummary}
              </p>
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-2 md:cht-grid-cols-4 cht-gap-6 cht-pt-4 cht-border-t cht-border-gray-50">
            <div className="cht-flex cht-flex-col cht-gap-1">
              <span className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                Duration
              </span>
              <span className="cht-text-sm cht-font-bold cht-text-[#1e293b] cht-flex cht-items-center cht-gap-2">
                <Clock size={14} className="cht-text-[#3e57da]" /> {duration || "Self-Paced"}
              </span>
            </div>
            <div className="cht-flex cht-flex-col cht-gap-1">
              <span className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                Outcome
              </span>
              <span className="cht-text-sm cht-font-bold cht-text-[#1e293b] cht-flex cht-items-center cht-gap-2">
                <Trophy size={14} className="cht-text-[#3e57da]" />{" "}
                {has_certificate !== false ? "Certificate" : `${coursesCount} Courses`}
              </span>
            </div>
            <div className="cht-flex cht-flex-col cht-gap-1">
              <span className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                Rating
              </span>
              <span className="cht-text-sm cht-font-bold cht-text-[#1e293b] cht-flex cht-items-center cht-gap-1.5">
                <Star size={14} className="cht-text-orange-400 cht-fill-orange-400" />
                {displayRating}
                <span className="cht-text-xs cht-text-gray-400 cht-font-medium">
                  ({displayReviews})
                </span>
              </span>
            </div>
            <div className="cht-flex cht-flex-col cht-gap-1">
              <span className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                Enrolled
              </span>
              <span className="cht-text-sm cht-font-bold cht-text-[#1e293b] cht-flex cht-items-center cht-gap-2">
                <Users size={14} className="cht-text-[#3e57da]" /> {displayEnrolled}
              </span>
            </div>
          </div>

          <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-between cht-gap-4 sm:cht-gap-0 cht-pt-2">
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-bg-indigo-50 cht-text-[#3e57da] cht-text-xs cht-font-bold">
                <Layers size={13} /> {coursesCount} {coursesCount === 1 ? "Course" : "Courses"}
              </span>
              <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-bg-slate-100 cht-text-slate-700 cht-text-xs cht-font-bold">
                <Book size={13} /> {totalLessons} {totalLessons === 1 ? "Lesson" : "Lessons"}
              </span>
            </div>
            <Button
              as={Link}
              to={`/student/dashboard/track/${slug}`}
              variant="secondary"
              isPill
              size="md"
              rightIcon={<ChevronRight size={16} />}
              className="cht-w-full sm:cht-w-auto"
            >
              Enroll in Path
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Courses = () => {
  const { data: tracks = [], isLoading: trackLoading } = useCourseTracksQuery();
  const { data: courses = [], isLoading: courseLoading } = useCoursesQuery();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCoursesCount, setVisibleCoursesCount] = useState(6);
  const [visibleTracksCount, setVisibleTracksCount] = useState(4);

  const tabs = [
    { id: "all", label: "All Content" },
    { id: "tracks", label: "Career Tracks" },
    { id: "courses", label: "Single Courses" }
  ];

  const displayTracks = tracks;
  const displayCourses = courses;

  const filteredTracks = (activeFilter === "courses" ? [] : (displayTracks || [])).filter(track =>
    track.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredCourses = (activeFilter === "tracks" ? [] : (displayCourses || [])).filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedTracks = filteredTracks.slice(0, visibleTracksCount);
  const paginatedCourses = filteredCourses.slice(0, visibleCoursesCount);

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Course Library">
        <NavTabs
          tabs={tabs}
          activeTab={activeFilter}
          onTabSelect={setActiveFilter}
        />
      </HeaderBar>

      <main className="cht-container cht-space-y-10">

        {/* Search Bar */}
        <div className="cht-relative cht-max-w-2xl cht-mx-auto cht-group">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 group-focus-within:cht-text-[#3e57da] cht-transition-colors" size={18} />
          <input
            type="text"
            placeholder="Search for courses or career tracks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-white cht-border cht-border-gray-100 cht-rounded-2xl cht-py-4 cht-pl-12 cht-pr-4 cht-text-sm cht-font-medium cht-focus:border-[#3e57da] cht-outline-none cht-transition-all"
          />
        </div>

        {/* Stats Row */}
        <div className="cht-flex cht-justify-end">
          <span className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">{filteredTracks.length + filteredCourses.length} Total Results</span>
        </div>

        {/* Tracks Section */}
        {filteredTracks.length > 0 && (
          <section className="cht-space-y-6">
            <div className="cht-flex cht-items-center cht-gap-3 cht-px-2">
              <Layers className="cht-text-[#3e57da]" size={20} />
              <h2 className="cht-text-xl cht-font-bold cht-text-[#1e293b]">Career Tracks</h2>
              <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-bg-gray-100 cht-px-2 cht-py-0.5 cht-rounded-md">{filteredTracks.length}</span>
            </div>

            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-8">
              {paginatedTracks.map((item) => (
                <TrackWideCard
                  key={item.id}
                  title={item.title}
                  level={item.level}
                  duration={item.duration}
                  image={item.track_img_url}
                  slug={item.slug}
                  description={item.description}
                  outline_snippet={item.outline_snippet}
                  rating={item.rating}
                  review_count={item.review_count}
                  enrolled_count={item.enrolled_count}
                  lessons_count={item.lessons_count}
                  course_in_track={item.course_in_track}
                  payment_type={item.payment_type}
                  online_price={item.online_price}
                  has_certificate={item.has_certificate}
                />
              ))}
            </div>

            {visibleTracksCount < filteredTracks.length && (
              <div className="cht-mt-6 cht-flex cht-justify-center">
                <Button
                  variant="ghost"
                  isPill
                  className="cht-w-full cht-bg-gray-100/80 cht-text-gray-900"
                  onClick={() => setVisibleTracksCount(prev => prev + 4)}
                >
                  Show More Career Tracks
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Individual Courses Section */}
        {filteredCourses.length > 0 && (
          <section className="cht-space-y-6">
            <div className="cht-flex cht-items-center cht-gap-3 cht-px-2">
              <Book className="cht-text-[#3e57da]" size={20} />
              <h2 className="cht-text-xl cht-font-bold cht-text-[#1e293b]">Standalone Courses</h2>
              <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-bg-gray-100 cht-px-2 cht-py-0.5 cht-rounded-md">{filteredCourses.length}</span>
            </div>

            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-8">
              {paginatedCourses.map((item) => {
                const courseLessonsCount =
                  item.modules?.reduce(
                    (sum: number, m: any) =>
                      sum +
                      (m.lessons?.length || 0) +
                      (m.exercises?.length || m.exercise_module?.length || 0),
                    0
                  ) || Number(item.lessons_count) || 0;
                const displayRating = Number(item.rating) > 0 ? Number(item.rating).toFixed(1) : "0.0";
                const displayReviews = item.review_count ?? 0;
                const displayEnrolled = item.enrolled_count ?? 0;
                const priceBadge =
                  item.payment_type === "Free"
                    ? "Free"
                    : item.online_price != null
                    ? `₦${Number(item.online_price).toLocaleString()}`
                    : item.payment_type || "Paid";

                return (
                  <div key={item.id} className="cht-group cht-bg-white cht-rounded-[24px] cht-overflow-hidden cht-shadow-[0_10px_30px_rgba(0,0,0,0.02)] cht-border cht-border-gray-100/50 hover:cht-shadow-xl hover:-cht-translate-y-1 cht-transition-all cht-duration-300 cht-flex cht-flex-col">
                    <div className="cht-aspect-video cht-relative cht-overflow-hidden">
                      <img src={item.course_img_url || 'https://via.placeholder.com/400x225'} alt={item.title} className="cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform cht-duration-500" />
                      <div className="cht-absolute cht-top-4 cht-right-4 cht-bg-white/90 cht-backdrop-blur cht-px-3 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-bold cht-text-[#1e293b] cht-shadow-sm cht-uppercase cht-tracking-wider">
                        {priceBadge}
                      </div>
                    </div>
                    <div className="cht-p-6 cht-space-y-4 cht-flex-1 cht-flex cht-flex-col cht-justify-between">
                      <div className="cht-space-y-3">
                        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-wider">
                          <span>{item.level}</span>
                          {item.duration && (
                            <>
                              <span className="cht-text-gray-300">•</span>
                              <span>{item.duration}</span>
                            </>
                          )}
                          {courseLessonsCount > 0 && (
                            <>
                              <span className="cht-text-gray-300">•</span>
                              <span>{courseLessonsCount} Lessons</span>
                            </>
                          )}
                        </div>
                        <h3 className="cht-text-lg cht-font-bold cht-text-[#1e293b] cht-line-clamp-2 cht-leading-tight group-hover:cht-text-[#3e57da] cht-transition-colors">
                          {item.title}
                        </h3>
                      </div>

                      <div className="cht-pt-2 cht-space-y-3">
                        <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-gray-500 cht-font-semibold">
                          <div className="cht-flex cht-items-center cht-gap-1">
                            <Star size={14} className="cht-text-orange-400 cht-fill-orange-400" />
                            <span className="cht-text-sm cht-font-bold cht-text-[#1e293b]">{displayRating}</span>
                            <span className="cht-text-xs cht-text-gray-400 cht-font-medium">({displayReviews})</span>
                          </div>
                          <span className="cht-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-semibold cht-text-gray-500">
                            <Users size={12} className="cht-text-[#3e57da]" />
                            {displayEnrolled} enrolled
                          </span>
                        </div>
                        <Button
                          as={Link}
                          to={`/student/dashboard/courses/${item.slug}`}
                          variant="secondary"
                          isPill
                          size="sm"
                          rightIcon={<ChevronRight size={14} />}
                          className="cht-w-full"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleCoursesCount < filteredCourses.length && (
              <div className="cht-mt-10 cht-flex cht-justify-center">
                <Button
                  variant="ghost"
                  isPill
                  className="cht-w-full cht-bg-gray-100/80 cht-text-gray-900"
                  onClick={() => setVisibleCoursesCount(prev => prev + 6)}
                >
                  Show More Standalone Courses
                </Button>
              </div>
            )}
          </section>
        )}

        {filteredTracks.length === 0 && filteredCourses.length === 0 && (
          <div className="cht-max-w-4xl cht-mx-auto cht-bg-white cht-rounded-[32px] cht-p-20 cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-6 cht-animate-in cht-fade-in cht-slide-in-from-bottom-4 cht-duration-500 cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50">
            <div className="cht-w-20 cht-h-20 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center">
              {searchQuery ? <Search className="cht-text-gray-300" size={32} /> : <Book className="cht-text-gray-300" size={32} />}
            </div>
            <div className="cht-space-y-2">
              <h3 className="cht-text-xl cht-font-bold cht-text-[#1e293b]">
                {searchQuery ? "No matches found" : "Library is empty"}
              </h3>
              <p className="cht-text-sm cht-text-gray-400 cht-max-w-xs">
                {searchQuery
                  ? `We couldn't find anything matching "${searchQuery}". Try a different search term or browse our categories.`
                  : "We are currently updating our course library. Please check back later for new content!"}
              </p>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="cht-text-[#3e57da] cht-text-sm cht-font-bold hover:cht-underline"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {(trackLoading || courseLoading) && (
          <div className="cht-flex cht-flex-col cht-items-center cht-justify-center cht-py-20 cht-space-y-4">
            <div className="cht-w-12 cht-h-12 cht-border-4 cht-border-[#3e57da]/20 cht-border-t-[#3e57da] cht-rounded-full cht-animate-spin"></div>
            <p className="cht-text-sm cht-font-bold cht-text-gray-400 cht-animate-pulse">Loading Library...</p>
          </div>
        )}

      </main>
    </section>
  );
};

export default Courses;
